import { Action, combineReducers, configureStore } from "@reduxjs/toolkit";
import {
    FLUSH,
    PAUSE,
    PERSIST,
    PersistConfig,
    PersistedState,
    PersistMigrate,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { APP_VERSION_PERSIST } from "@/config";
import { openReplayService } from "@/Services/OpenReplay";
import {
    fullReset,
    logoutResetApp,
    restoreConfigFromServer,
    restoreProgressFromServer,
    safeReset,
} from "@/store/actions";
import {
    configTrackingMiddleware,
    opfsQueueMiddleware,
    progressTrackingMiddleware,
    requestQueueTailListener,
} from "@/store/middleware";

import { authReducer } from "./slices/auth";
import { configReducer } from "./slices/config";
import { hrReducer } from "./slices/hr";
import { offlineReducer } from "./slices/offline";
import { progressReducer } from "./slices/progress";
import { svReducer } from "./slices/sv";
import { themeReducer } from "./slices/theme";
import { uiReducer } from "./slices/ui";
import { userReducer } from "./slices/user";
import { workReducer } from "./slices/work";

const appReducer = combineReducers({
    auth: authReducer,
    theme: themeReducer,
    user: userReducer,
    work: workReducer,
    sv: svReducer,
    hr: hrReducer,
    offline: offlineReducer,
    ui: uiReducer,
    config: configReducer,
    progress: progressReducer,
});

type AppState = ReturnType<typeof appReducer>;

const createResetState = (
    state: AppState | undefined,
    preserveConfig: {
        auth?: boolean;
        user?: boolean;
        progress?: boolean;
        offline?: boolean;
        work?: boolean;
        sv?: boolean;
    },
): AppState => {
    const initialState = appReducer(undefined, { type: "@@INIT" });

    const resetState: AppState = { ...initialState };

    resetState.auth = state.auth;

    if (preserveConfig.user && state?.user) {
        resetState.user = state.user;
    }

    if (preserveConfig.work && state?.work) {
        resetState.work = state.work;
    }

    if (preserveConfig.progress && state?.progress) {
        resetState.progress = state.progress;
    }

    if (preserveConfig.sv && state?.sv) {
        resetState.sv = state.sv;
    }

    if (preserveConfig.offline && state?.offline) {
        resetState.offline = {
            ...initialState.offline,
            requestQueue: state.offline?.requestQueue,
            progress: state.offline?.progress,
        };
    }

    if (state?.config) {
        resetState.config = state.config;
    }

    return resetState;
};

export const rootReducer = (
    state: AppState | undefined,
    action: Action,
): AppState => {
    if (logoutResetApp.match(action)) {
        const resetState = createResetState(state, {
            progress: true,
            offline: true,
        });
        return appReducer(resetState, action);
    }

    if (safeReset.match(action)) {
        const resetState = createResetState(state, {
            user: true,
            progress: true,
            offline: true,
            work: true,
            sv: true,
        });
        return appReducer(resetState, action);
    }

    if (fullReset.match(action)) {
        const resetState = createResetState(state, {
            user: true,
        });
        return appReducer(resetState, action);
    }

    if (restoreProgressFromServer.match(action)) {
        const { progress, requestQueue } = action.payload;

        const baseState = state ?? appReducer(undefined, { type: "@@INIT" });

        return {
            ...baseState,
            progress,
            offline: {
                ...baseState.offline,
                requestQueue,
            },
        };
    }

    if (restoreConfigFromServer.match(action)) {
        const config = action.payload;

        const baseState = state ?? appReducer(undefined, { type: "@@INIT" });

        return {
            ...baseState,
            config,
        };
    }

    return appReducer(state, action);
};

const migrate: PersistMigrate = async (state: PersistedState) => {
    if (!state || typeof state !== "object" || !("_persist" in state)) {
        return state;
    }

    const persistedVersion = state._persist.version;
    const shouldReset = persistedVersion !== APP_VERSION_PERSIST;

    if (shouldReset) {
        return {
            _versionChanged: true,
            _persist: {
                ...state._persist,
                version: APP_VERSION_PERSIST,
            },
        };
    }

    return state;
};

const persistConfig: PersistConfig<AppState> = {
    key: "root",
    storage,
    blacklist: ["auth"],
    version: APP_VERSION_PERSIST,
    migrate,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export function setupStore(preloadedState?: Partial<AppState>) {
    return configureStore({
        reducer: persistedReducer,
        preloadedState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [
                        FLUSH,
                        REHYDRATE,
                        PAUSE,
                        PERSIST,
                        PURGE,
                        REGISTER,
                    ],
                },
            })
                .prepend(requestQueueTailListener.middleware)
                .concat(
                    progressTrackingMiddleware,
                    configTrackingMiddleware,
                    opfsQueueMiddleware,
                    openReplayService.reduxMiddleware,
                ),
    });
}

export const store = setupStore();
export const persistor = persistStore(store);
